/**
 * Chat API module for the AgileAIDev ChatBot
 * Provides functions for sending messages and receiving responses
 */

/**
 * Custom hook for chat API interactions
 * @param {Object} options - Configuration options
 * @param {string} options.apiUrl - Base URL for API endpoints
 * @param {boolean} options.streaming - Whether to use streaming responses
 * @returns {Object} API methods for chat interactions
 */
export const useChatApi = (options = {}) => {
  const {
    apiUrl = '/api/chat',
    streaming = false,
  } = options;

  /**
   * Sends a message to the chat API
   * @param {string} message - Message text to send
   * @param {Object} context - Optional context information for the message
   * @returns {Promise<Object>} Response from the API
   */
  const sendMessage = async (message, context = null) => {
    const payload = { message };
    
    // Add context if provided
    if (context) {
      payload.context = context;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw {
          status: response.status,
          message: `API Error: ${response.statusText}`,
          details: errorData.error || 'Unknown error',
        };
      }

      return await response.json();
    } catch (error) {
      // If it's already our custom error object, throw it directly
      if (error.status) {
        throw error;
      }
      // Otherwise, wrap and throw the error
      throw error;
    }
  };

  /**
   * Sends feedback for a specific message
   * @param {string} messageId - ID of the message to provide feedback for
   * @param {string} feedback - Feedback type (e.g., 'positive', 'negative')
   * @param {string} comment - Optional comment with the feedback
   * @returns {Promise<Object>} Response from the API
   */
  const sendFeedback = async (messageId, feedback, comment = '') => {
    try {
      const response = await fetch(`${apiUrl}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messageId,
          feedback,
          comment,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send feedback');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  };

  /**
   * Sends a message and handles streaming response
   * @param {string} message - Message text to send
   * @param {Function} onChunk - Callback for each chunk of streaming response
   * @param {Object} context - Optional context information for the message
   * @returns {Promise<Object>} Complete response after streaming
   */
  const sendStreamingMessage = async (message, onChunk, context = null) => {
    const payload = { message };
    
    // Add context if provided
    if (context) {
      payload.context = context;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/x-ndjson',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw {
          status: response.status,
          message: `API Error: ${response.statusText}`,
          details: errorData.error || 'Unknown error',
        };
      }

      // Check if response is streaming (content-type: application/x-ndjson)
      const contentType = response.headers.get('Content-Type') || '';
      
      if (contentType.includes('application/x-ndjson')) {
        // Handle streaming response
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let responseContent = '';
        
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            break;
          }
          
          // Decode the chunk and process each line
          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n').filter(line => line.trim());
          
          for (const line of lines) {
            try {
              const data = JSON.parse(line);
              
              // Update accumulated response
              if (data.content) {
                responseContent += data.content;
              }
              
              // Call the chunk callback
              onChunk(data);
            } catch (e) {
              console.error('Error parsing streaming response chunk:', e);
            }
          }
        }
        
        // Return the complete response
        return { content: responseContent };
      } else {
        // Handle non-streaming response
        const data = await response.json();
        onChunk(data);
        return data;
      }
    } catch (error) {
      // If it's already our custom error object, throw it directly
      if (error.status) {
        throw error;
      }
      // Otherwise, wrap and throw the error
      throw error;
    }
  };

  return {
    sendMessage,
    sendFeedback,
    sendStreamingMessage,
  };
};

export default useChatApi; 